import React from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Banner,
  Spinner,
  Tooltip,
} from 'components';
import { useAppManager } from 'core/hooks';
import { routerStore } from 'core';
import type CreateShipmentQuoteAppManager from 'apps/shipmentQuote/appManager';
import { ProductQuoteAppManager } from './productQuoteAppManager';

import { FrameHeaderHeight } from '../../styles/constants';
import { ProductQuoteHeader } from './ProductQuoteHeader';

import { NewProductQuote } from './NewProductQuote';
import { columns } from './ProductQuoteTables';
import { ProductQuoteApp } from './app';
import { TableSection } from './ProductQuoteTables/TableSection';

const CloseSidebarButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Button text='Done' onClick={onClick} />
    </Box>
  );
};

function getCheckBoxColumn(
  shipmentQuoteAppManager: CreateShipmentQuoteAppManager,
) {
  return {
    Header: '',
    accessor: '',
    Cell: function checkbox(productQuote: ProductQuoteApp) {
      const handleChange = () => {
        // change the URL to add the ProductQuote ids
        shipmentQuoteAppManager.AddOrRemoveProductQuoteId(productQuote);
      };
      const pqids = routerStore.params.get('pqids') || [];
      const isPQFromUrl = pqids.includes(productQuote.dgraphId);

      const hasDifferentCurrency =
        shipmentQuoteAppManager.shipmentQuoteApp.currency &&
        shipmentQuoteAppManager.shipmentQuoteApp.currency !==
          productQuote.currency;
      const hasDifferentSupplier =
        shipmentQuoteAppManager.shipmentQuoteApp.manufacturer?.id &&
        shipmentQuoteAppManager.shipmentQuoteApp.manufacturer.id !==
          productQuote.supplier.id;
      const disabled =
        (hasDifferentCurrency || hasDifferentSupplier) && !isPQFromUrl;

      let tooltipText = null;
      let tooltipItem = null;
      if (hasDifferentCurrency) {
        tooltipText = 'Does not match currency';
        tooltipItem = `(${shipmentQuoteAppManager.shipmentQuoteApp.currency})`;
      }
      if (hasDifferentSupplier) {
        tooltipText = 'Does not match supplier';
        tooltipItem = `(${shipmentQuoteAppManager.shipmentQuoteApp.manufacturer?.companyName})`;
      }
      const tooltipId = `tooltipid-${productQuote.id}`;
      return (
        <>
          <Tooltip delayShow={50} place='bottom' id={tooltipId}>
            <p>{tooltipText}</p>
            <p>{tooltipItem}</p>
          </Tooltip>
          <div data-for={tooltipId} data-tip={tooltipText}>
            <Checkbox
              onChange={handleChange}
              checked={isPQFromUrl}
              value={productQuote.dgraphId}
              id={productQuote.dgraphId}
              disabled={disabled}
            />
          </div>
        </>
      );
    },
  };
}

const RowActionButtons = ({ node }: { node: ProductQuoteApp }) => {
  const handleRemoveClick = () => {
    node.SetDeleteInitiated(true);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        variant='destructive'
        size='small'
        outline
        icon='delete'
        margin='0 8px 0 0'
        onClick={handleRemoveClick}
      />
    </Box>
  );
};

interface ProductQuoteProps {
  shipmentQuoteAppManager: CreateShipmentQuoteAppManager;
  productQuoteApp: ProductQuoteApp;
  onModalClose: () => void;
  // t: (key: string) => string;
}

// TODO: add namespace here
export const ProductQuotesSideBar = observer(function ProductQuotesSideBar({
  shipmentQuoteAppManager,
  productQuoteApp,
  onModalClose,
}: ProductQuoteProps) {
  const productQuoteAppManager = useAppManager(ProductQuoteAppManager, null, {
    shipmentQuoteAppManager,
    fromRFQ: productQuoteApp.from,
  });
  const finalColumns = columns.slice(3);
  finalColumns.splice(0, 0, getCheckBoxColumn(shipmentQuoteAppManager));
  const productQuotes = productQuoteAppManager.productQuotes || [];
  const { loading } = productQuoteAppManager.productQuotesQ;
  const warningMessage =
    shipmentQuoteAppManager.numSelectedPqs > 1 ? (
      <Box sx={{ width: '100%' }}>
        <Banner variant='warning' size='small'>
          Warning: you have added more than one quote for the same RFQ.
          That&apos;s unusual but valid if e.g. you are quoting both a mould and
          a production run.
        </Banner>
      </Box>
    ) : null;

  return (
    <Dialog
      open={true}
      onClose={onModalClose}
      sx={{ top: FrameHeaderHeight, right: 0 }}
    >
      <Box p={4}>
        <ProductQuoteHeader productQuoteApp={productQuoteApp} />
        {(productQuotes.length !== 0 && (
          <TableSection
            loading={false}
            columns={finalColumns}
            nodes={productQuotes}
            title='Part Quotes' // t('Part Quotes'')
            RowActionButtons={RowActionButtons}
            emptyTablePlaceholder={null}
            warningMessage={warningMessage}
            Button={<CloseSidebarButton onClick={onModalClose} />}
          />
        )) ||
          (loading && <Spinner margin='1rem 0' />)}
        <NewProductQuote
          inRfqPage={false}
          productQuoteManager={productQuoteAppManager}
        />
      </Box>
    </Dialog>
  );
});
